import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PROCESSING_STAGES,
  PROCESSING_STAGE_LABELS,
  ROLE_LABELS,
} from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Badge, Button, Card, ErrorBanner, Modal, Spinner, inputCls } from '../../components/ui';
import { Icon } from '../../components/Icon';
import {
  ActorCell,
  describeEvent,
  formatTime,
  formatWhen,
  hasDiscrepancy,
  type TimelineResponse,
} from './AccountabilityPage';

/**
 * Phase 2G.5 — Discrepancy Investigation & Resolution visibility.
 *
 * A pure read of the admin-only /api/discrepancies endpoints: WHICH
 * transactions recorded an item-count difference, AT WHICH stage, with
 * what expected/actual/signed difference, recorded BY whom (or "Not
 * recorded"), WHEN and WHERE. The transaction drill-down lists a
 * transaction's count records chronologically and embeds the existing
 * stage timeline (/api/accountability/transactions/:ref) - no second
 * timeline is built.
 *
 * Deliberately factual: no blame, severity, cause or resolution status is
 * invented. The schema stores no resolution record, so every discrepancy
 * shows the honest label "Unresolved in recorded data"; a later count
 * never overwrites an earlier one, and differences are never summed into
 * a "missing items" total.
 */

interface DiscrepancyActorDto {
  id: string | null;
  name: string | null;
  role: string | null;
}

interface DiscrepancyRecordDto {
  id: string;
  transaction: { id: string; number: string; status: string; statusLabel: string };
  branch: { id: string; code: string };
  stage: string;
  stageLabel: string;
  machine: string | null;
  employee: DiscrepancyActorDto | null;
  expectedCount: number | null;
  actualCount: number | null;
  difference: number | null;
  isDiscrepancy: boolean;
  startedAt: string;
  completedAt: string;
  recordedAt: string;
  note: string | null;
  resolution: { status: string; label: string } | null;
  transactionDiscrepancies: number;
}

interface DiscrepanciesResponse {
  total: number;
  transactions: number;
  limit: number;
  offset: number;
  timeZone: string;
  range: { from: string | null; to: string | null } | null;
  discrepancyOnly: boolean;
  items: DiscrepancyRecordDto[];
}

interface TransactionViewResponse {
  timeZone: string;
  transaction: {
    id: string;
    number: string;
    status: string;
    statusLabel: string;
    fulfillmentMethod: string;
    branch: { id: string; code: string };
    customerName: string;
    itemCount: number;
    createdAt: string;
  };
  discrepancyCount: number;
  records: DiscrepancyRecordDto[];
}

interface UserOptionDto {
  id: string;
  name: string;
  username: string;
  role: keyof typeof ROLE_LABELS;
  isActive: boolean;
}

const PAGE_SIZE = 20;

/** Signed exactly as recorded: negative stays negative, zero stays zero. */
function formatDifference(difference: number | null): string {
  if (difference === null) return 'not recorded';
  if (difference > 0) return `+${difference}`;
  return String(difference);
}

/** Manila calendar date for the "Today" shortcut (server re-parses it as Manila). */
function manilaToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila' }).format(new Date());
}

const NOT_RECORDED = <span className="italic text-slate-400">Not recorded</span>;

function RecordedByCell({ actor }: { actor: DiscrepancyActorDto | null }) {
  if (!actor || (!actor.name && !actor.role)) return NOT_RECORDED;
  return (
    <span>
      <span className="font-medium text-slate-900">{actor.name || 'Not recorded'}</span>
      {actor.role && (
        <span className="mt-0.5 block text-xs text-slate-500">
          {(ROLE_LABELS as Record<string, string>)[actor.role] ?? actor.role}
        </span>
      )}
    </span>
  );
}

function DifferenceCell({ difference }: { difference: number | null }) {
  if (difference === null) return <span className="text-slate-400">not recorded</span>;
  if (difference === 0) return <span className="tabular-nums text-slate-500">0</span>;
  return (
    <span className="font-semibold tabular-nums text-amber-700">
      {formatDifference(difference)}
    </span>
  );
}

export function DiscrepancyPage() {
  const { branchFilter, setBranchFilter, branches } = useAuth();

  const [data, setData] = useState<DiscrepanciesResponse | null>(null);
  const [employees, setEmployees] = useState<UserOptionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // View filters — every one is re-validated server-side on each request.
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [transactionFilter, setTransactionFilter] = useState('');
  const [discrepancyOnly, setDiscrepancyOnly] = useState(true);
  const [page, setPage] = useState(0);

  // Transaction investigation modal: the transaction's count records plus
  // the EXISTING stage timeline (2G.3 endpoint) - fetched independently so
  // one failure never hides the other.
  const [investigateRef, setInvestigateRef] = useState<string | null>(null);
  const [view, setView] = useState<TransactionViewResponse | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null);
  const [timelineError, setTimelineError] = useState<string | null>(null);

  const filterParams = () => {
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (employeeFilter) params.set('employee', employeeFilter);
    if (stageFilter) params.set('stage', stageFilter);
    if (fromFilter) params.set('from', fromFilter);
    if (toFilter) params.set('to', toFilter);
    if (transactionFilter) params.set('transaction', transactionFilter);
    params.set('discrepancyOnly', discrepancyOnly ? 'true' : 'false');
    return params;
  };

  // Employee dropdown: branch-scoped server-side (admin passes the view filter).
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    params.set('includeInactive', 'true');
    void api
      .get<{ users: UserOptionDto[] }>(`/users?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setEmployees(res.users);
      })
      .catch(() => {
        if (!cancelled) setEmployees([]);
      });
    return () => {
      cancelled = true;
    };
  }, [branchFilter, refreshKey]);

  // Discrepancy list (paginated, deterministic: recorded time desc).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = filterParams();
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(page * PAGE_SIZE));
    void api
      .get<DiscrepanciesResponse>(`/discrepancies?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load discrepancies.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    branchFilter,
    employeeFilter,
    stageFilter,
    fromFilter,
    toFilter,
    transactionFilter,
    discrepancyOnly,
    page,
    refreshKey,
  ]);

  // Investigation modal: transaction records + existing stage timeline.
  useEffect(() => {
    if (!investigateRef) {
      setView(null);
      setViewError(null);
      setTimeline(null);
      setTimelineError(null);
      return;
    }
    let cancelled = false;
    setView(null);
    setViewError(null);
    setTimeline(null);
    setTimelineError(null);
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    const qs = params.toString();
    void api
      .get<TransactionViewResponse>(
        `/discrepancies/transactions/${encodeURIComponent(investigateRef)}${qs ? `?${qs}` : ''}`,
      )
      .then((res) => {
        if (!cancelled) setView(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setViewError(
            err instanceof ApiError ? err.message : 'Failed to load discrepancy records.',
          );
        }
      });
    void api
      .get<TimelineResponse>(
        `/accountability/transactions/${encodeURIComponent(investigateRef)}${qs ? `?${qs}` : ''}`,
      )
      .then((res) => {
        if (!cancelled) setTimeline(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setTimelineError(err instanceof ApiError ? err.message : 'Failed to load the timeline.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [investigateRef, branchFilter, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const resetPageAnd = (fn: () => void) => {
    setPage(0);
    fn();
  };

  const scopeLabel = branchFilter === 'ALL' ? 'All branches' : `Branch ${branchFilter}`;
  const total = data?.total ?? 0;
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, total);
  const hasFilters = Boolean(
    employeeFilter || stageFilter || fromFilter || toFilter || transactionFilter,
  );

  return (
    <section className="space-y-4" aria-label="Discrepancy investigation">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Discrepancy Investigation
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {scopeLabel} · recorded item-count differences by stage — read-only, from immutable
            records the system already keeps.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={refresh}>
            <Icon name="refresh" className="h-5 w-5" />
            Refresh
          </Button>
        </div>
      </div>

      <Card className="flex items-start gap-3 p-4">
        <Icon name="shield" className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
        <p className="text-sm text-slate-500">
          This is an <span className="font-semibold text-slate-700">investigation, not blame</span>{' '}
          — every row is a fact recorded at the moment of the work, never rewritten. Differences
          are shown signed exactly as recorded, zero-difference counts stay visible, and counts are
          never summed across stages into a “missing items” total. The system stores no resolution
          record, so every discrepancy shows{' '}
          <span className="font-semibold text-slate-700">Unresolved in recorded data</span> until a
          resolution is genuinely recorded somewhere. Missing actors appear as{' '}
          <span className="italic">Not recorded</span> instead of being guessed.
        </p>
      </Card>

      {error && <ErrorBanner message={error} />}

      {/* Filters — branch mirrors the header switcher; the rest re-validated server-side. */}
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <label className="flex min-w-44 flex-col gap-1 text-xs font-semibold text-slate-500">
          Branch
          <select
            value={branchFilter}
            onChange={(e) => resetPageAnd(() => setBranchFilter(e.target.value))}
            className={inputCls}
          >
            <option value="ALL">All Branches</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.code}>
                {branch.code} — {branch.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-48 flex-col gap-1 text-xs font-semibold text-slate-500">
          Employee
          <select
            value={employeeFilter}
            onChange={(e) => resetPageAnd(() => setEmployeeFilter(e.target.value))}
            className={inputCls}
          >
            <option value="">All employees</option>
            {employees.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.username}){user.isActive ? '' : ' — inactive'}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-44 flex-col gap-1 text-xs font-semibold text-slate-500">
          Processing stage
          <select
            value={stageFilter}
            onChange={(e) => resetPageAnd(() => setStageFilter(e.target.value))}
            className={inputCls}
          >
            <option value="">All stages</option>
            {PROCESSING_STAGES.map((stage) => (
              <option key={stage} value={stage}>
                {PROCESSING_STAGE_LABELS[stage]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-40 flex-col gap-1 text-xs font-semibold text-slate-500">
          From
          <input
            type="date"
            value={fromFilter}
            onChange={(e) => resetPageAnd(() => setFromFilter(e.target.value))}
            className={inputCls}
          />
        </label>
        <label className="flex min-w-40 flex-col gap-1 text-xs font-semibold text-slate-500">
          To
          <input
            type="date"
            value={toFilter}
            onChange={(e) => resetPageAnd(() => setToFilter(e.target.value))}
            className={inputCls}
          />
        </label>
        <Button
          variant="secondary"
          onClick={() =>
            resetPageAnd(() => {
              setFromFilter(manilaToday());
              setToFilter(manilaToday());
            })
          }
        >
          Today
        </Button>
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            resetPageAnd(() => setTransactionFilter(searchInput.trim()));
          }}
        >
          <label className="flex min-w-56 flex-col gap-1 text-xs font-semibold text-slate-500">
            Transaction reference
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="LH1-000001"
              className={inputCls}
            />
          </label>
          <Button variant="secondary" type="submit">
            <Icon name="search" className="h-5 w-5" />
            Search
          </Button>
        </form>
        <label className="flex items-center gap-2 pb-3 text-sm font-semibold text-slate-600">
          <input
            type="checkbox"
            checked={discrepancyOnly}
            onChange={(e) => resetPageAnd(() => setDiscrepancyOnly(e.target.checked))}
            className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          />
          Discrepancy-only results
        </label>
        {hasFilters && (
          <Button
            variant="secondary"
            onClick={() =>
              resetPageAnd(() => {
                setEmployeeFilter('');
                setStageFilter('');
                setFromFilter('');
                setToFilter('');
                setSearchInput('');
                setTransactionFilter('');
              })
            }
          >
            Clear filters
          </Button>
        )}
        <span className="ml-auto text-xs font-medium text-slate-400">
          Dates are Manila business dates (Asia/Manila), applied to when the count was recorded
          {data?.range ? ` · ${data.range.from ?? '…'} → ${data.range.to ?? '…'}` : ''}
        </span>
      </Card>

      {/* Results */}
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold text-slate-700">
            {discrepancyOnly ? 'Recorded discrepancies' : 'Recorded count records'}
          </h2>
          {data && total > 0 && (
            <p className="text-sm text-slate-500">
              {discrepancyOnly
                ? `${total} discrepancy event${total === 1 ? '' : 's'}`
                : `${total} count record${total === 1 ? '' : 's'}`}{' '}
              across {data.transactions} transaction{data.transactions === 1 ? '' : 's'} · showing{' '}
              {from}–{to}, newest first
            </p>
          )}
        </div>

        {loading && !data ? (
          <div className="flex items-center gap-3 py-6 text-sm text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading discrepancies…
          </div>
        ) : !data || data.items.length === 0 ? (
          <Card className="mt-2 flex flex-col items-center p-6 text-center">
            <Icon name="check" className="h-8 w-8 text-emerald-400" />
            <p className="mt-2 font-medium text-slate-700">No records match these filters.</p>
            <p className="mt-1 text-sm text-slate-500">
              {hasFilters
                ? 'Clear a filter to widen the search.'
                : discrepancyOnly
                  ? 'No item-count discrepancies are recorded in this scope.'
                  : 'No completed count records exist in this scope yet.'}
            </p>
          </Card>
        ) : (
          <>
            {/* Desktop table */}
            <div className="mt-2 overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                  <tr>
                    <th className="px-4 py-3">Recorded at</th>
                    <th className="px-4 py-3">Transaction</th>
                    <th className="px-4 py-3">Stage</th>
                    <th className="px-3 py-3 text-right">Expected</th>
                    <th className="px-3 py-3 text-right">Actual</th>
                    <th className="px-3 py-3 text-right">Difference</th>
                    <th className="px-4 py-3">Recorded by</th>
                    <th className="px-4 py-3">Branch</th>
                    <th className="px-4 py-3">Resolution</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.items.map((row) => (
                    <tr key={row.id} className="align-top hover:bg-slate-100/70">
                      <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                        {formatWhen(row.completedAt)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setInvestigateRef(row.transaction.number)}
                          className="font-semibold text-indigo-600 underline-offset-2 hover:underline"
                          title="Investigate this transaction"
                        >
                          {row.transaction.number}
                        </button>
                        <p className="mt-0.5 flex flex-wrap items-center gap-1">
                          <Badge tone="indigo">{row.transaction.statusLabel}</Badge>
                          {row.transactionDiscrepancies > 1 && (
                            <Badge tone="amber">
                              {row.transactionDiscrepancies} discrepancy events
                            </Badge>
                          )}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {row.stageLabel}
                        {row.machine && (
                          <span className="mt-0.5 block text-xs text-slate-500">{row.machine}</span>
                        )}
                        {row.note && (
                          <span className="mt-0.5 block max-w-56 text-xs text-slate-400">
                            {row.note}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-700">
                        {row.expectedCount ?? <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-700">
                        {row.actualCount ?? <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <DifferenceCell difference={row.difference} />
                      </td>
                      <td className="px-4 py-3">
                        <RecordedByCell actor={row.employee} />
                      </td>
                      <td className="px-4 py-3">
                        <Badge>{row.branch.code}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        {row.resolution ? (
                          <Badge tone="amber">{row.resolution.label}</Badge>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                        {row.isDiscrepancy && (
                          <Link
                            to={`/admin/control-review?${new URLSearchParams({
                              category: 'ITEM_COUNT_DISCREPANCY',
                              module: 'PROCESSING',
                              ref: row.id,
                              title: `${row.stageLabel} count difference on ${row.transaction.number}`,
                            }).toString()}`}
                            className="mt-1 block text-xs font-medium text-indigo-600 hover:underline"
                          >
                            Open review case
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>


            {/* Deterministic pagination */}
            {total > PAGE_SIZE && (
              <div className="mt-3 flex items-center justify-between">
                <Button
                  variant="secondary"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-500">
                  Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                </span>
                <Button
                  variant="secondary"
                  disabled={(page + 1) * PAGE_SIZE >= total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Investigation modal: loading / error / records + existing timeline */}
      {investigateRef && !view && !viewError && (
        <Modal title={`Transaction ${investigateRef}`} onClose={() => setInvestigateRef(null)}>
          <div className="flex items-center justify-center gap-3 py-8 text-sm text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading investigation…
          </div>
        </Modal>
      )}
      {investigateRef && viewError && (
        <Modal title={`Transaction ${investigateRef}`} onClose={() => setInvestigateRef(null)}>
          <ErrorBanner message={viewError} />
        </Modal>
      )}
      {investigateRef && view && (
        <Modal title={`Investigate ${view.transaction.number}`} onClose={() => setInvestigateRef(null)}>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
              <Badge>{view.transaction.branch.code}</Badge>
              <Badge tone="indigo">{view.transaction.statusLabel}</Badge>
              <span>{view.transaction.customerName}</span>
              <span className="text-slate-400">{view.transaction.itemCount} items</span>
              <Badge tone={view.discrepancyCount > 0 ? 'amber' : 'slate'}>
                {view.discrepancyCount} discrepanc{view.discrepancyCount === 1 ? 'y' : 'ies'}{' '}
                recorded
              </Badge>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-700">
                Count records on this transaction
              </h3>
              <p className="mt-0.5 text-xs text-slate-500">
                Chronological (oldest first), exactly as recorded — a later count never resolves an
                earlier one.
              </p>
              {view.records.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">
                  No completed count records exist for this transaction.
                </p>
              ) : (
                <ol className="mt-2 space-y-3">
                  {view.records.map((record) => (
                    <li
                      key={record.id}
                      className={`rounded-xl border p-3 text-sm ${
                        record.isDiscrepancy
                          ? 'border-amber-200 bg-amber-50 text-amber-900'
                          : 'border-slate-200 bg-slate-50 text-slate-700'
                      }`}
                    >
                      <p className="flex flex-wrap items-center gap-2 font-semibold">
                        {record.stageLabel}
                        {record.machine ? ` · ${record.machine}` : ''}
                        {record.isDiscrepancy && <Badge tone="amber">Discrepancy</Badge>}
                      </p>
                      <p className="mt-1 tabular-nums">
                        Expected {record.expectedCount ?? 'not recorded'} · counted{' '}
                        {record.actualCount ?? 'not recorded'} · difference{' '}
                        {formatDifference(record.difference)}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs">
                        <RecordedByCell actor={record.employee} />
                        <span>
                          {record.completedAt ? formatWhen(record.completedAt) : 'completion time not recorded'}
                        </span>
                      </div>
                      {record.resolution && (
                        <p className="mt-1 text-xs opacity-80">{record.resolution.label}</p>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div className="border-t border-slate-100 pt-3">
              <h3 className="text-sm font-semibold text-slate-700">Existing stage timeline</h3>
              <p className="mt-0.5 text-xs text-slate-500">
                The full accountability trail already recorded for this transaction (read-only).
              </p>
              {timelineError ? (
                <div className="mt-2">
                  <ErrorBanner message={timelineError} />
                </div>
              ) : !timeline ? (
                <div className="mt-2 flex items-center gap-3 py-4 text-sm text-slate-500">
                  <Spinner className="h-5 w-5 text-indigo-600" /> Loading timeline…
                </div>
              ) : (
                <ol className="mt-2 space-y-3">
                  {timeline.items.map((event) => (
                    <li key={event.id} className="flex gap-3">
                      <span className="w-20 shrink-0 pt-0.5 text-right text-xs tabular-nums text-slate-400">
                        {formatTime(event.occurredAt)}
                      </span>
                      <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-indigo-400" aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-slate-800">
                            {event.kindLabel}
                          </span>
                          <span className="text-sm text-slate-600">
                            <ActorCell actor={event.actor} />
                          </span>
                          {hasDiscrepancy(event) && <Badge tone="amber">Recorded item discrepancy</Badge>}
                        </div>
                        <p className="mt-0.5 text-sm text-slate-600">{describeEvent(event)}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}
